import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import HomeHeader from './homeheader.components'
import PreorderForm from '../../components/forms/preorder.components'
import cupcake from '../../assets/images/cupcakes.png'
import wedcake from '../../assets/images/wedding-cakes.png'
import cakeBg from '../../assets/images/cake_bg.jpg'
import cakeBg2 from '../../assets/images/cake_bg2.jpg'
import ContactForm from '../../components/contact/contactform.components'
import { OrderContext } from '../../context/order.context'
import './homepage.styles.scss'

const defaultCompanyValues = {
  companyDisplayName: 'Cake by Sandy',
  companyName: 'Cake by Sandy',
  slogan: 'Deliciously baked, lovingly made.',
  address1: '',
  address2: '',
  phone: '',
  email: '',
  country: '',
  ig: '',
  fb: '',
  tw: '',
  tq: '',
  yt: '',
  logo: '',
  createdAt: new Date(),
}

const defaultValues = {
  title: 'Deliciously Baked, Lovingly Made',
  subtitle1: 'Handcrafted cakes and pastries that bring joy to every occasion,',
  subtitle2: 'made with the finest ingredients and a touch of love.',
  cat1: 'Wedding Cakes',
  cat1_desc: 'Elegant tiers and custom designs for your celebration.',
  cat1_img: '',
  cat2: 'Birthday Cakes',
  cat2_desc: 'Festive flavors sized for every party and guest list.',
  cat2_img: '',
  cat3: 'Custom Cakes',
  cat3_desc: 'Tell us your vision. We bake it to order.',
  cat3_img: '',
  featured_img_1: '',
  featured_img_2: '',
  featured_img_3: '',
  more_sandy:
    "From decadent cakes to mouthwatering pastries, every creation is crafted with passion. Explore the gallery, book your custom order, and enjoy baked goods made with love.",
  company: defaultCompanyValues,
  del: 'no',
  created_at: new Date(),
  updated_at: null,
}

/** Original hardcoded landing images — always available as fallbacks */
const HARDCODED_CATEGORY_IMAGES = [
  'https://i.pinimg.com/564x/16/fb/99/16fb9968fcc654addbc7a21ade300d3a.jpg',
  'https://i.pinimg.com/564x/6b/3a/6e/6b3a6e4dc26aca5a3df6247b07d78d24.jpg',
  'https://i.pinimg.com/564x/5e/21/94/5e2194154e9a83fff4cca190085f8984.jpg',
]

const HARDCODED_FEATURED_IMAGES = [
  'https://c4.wallpaperflare.com/wallpaper/643/821/128/spikelets-bread-cakes-roll-wallpaper-preview.jpg',
  'https://www.southernliving.com/thmb/pvi50-Vck49MR4vJ4Eh8-kOF27E=/1200x0/filters:no_upscale():max_bytes(150000):strip_icc()/gettyimages-639897672-2000-934de272fa7c42f5ba0516978022e05c.jpg',
  'https://dynl.mktgcdn.com/p/rtP4FfaNFgvmmrVXUHiqpuu5sG2CbPvIfxA4W5kzWKY/619x275.jpg',
]

const HARDCODED_BDAY =
  'https://firebasestorage.googleapis.com/v0/b/cakebysandy-a4e24.appspot.com/o/files%2Fbday.png?alt=media&token=bde32ec8-f3e5-44d0-9995-7839ea0e1140'

const LOCAL_CATEGORY_FALLBACKS = [wedcake, cupcake, cakeBg2]
const LOCAL_FEATURED_FALLBACKS = [cakeBg, cakeBg2, wedcake]

const pickSrc = (...candidates) => {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

const SafeImage = ({ src, fallbackSrc, alt, className }) => {
  const [current, setCurrent] = useState(src || fallbackSrc)

  useEffect(() => {
    setCurrent(src || fallbackSrc)
  }, [src, fallbackSrc])

  return (
    <img
      src={current}
      alt={alt}
      className={className || undefined}
      onError={() => {
        if (fallbackSrc && current !== fallbackSrc) {
          setCurrent(fallbackSrc)
        }
      }}
    />
  )
}

const HomePage = () => {
  const { curPage } = useContext(OrderContext)
  const [page, setPage] = useState(defaultValues)

  useEffect(() => {
    if (curPage) {
      setPage({
        ...defaultValues,
        ...curPage,
        company:
          curPage.company && !Array.isArray(curPage.company)
            ? { ...defaultCompanyValues, ...curPage.company }
            : defaultCompanyValues,
      })
    }
  }, [curPage])

  const {
    title,
    subtitle1,
    subtitle2,
    cat1,
    cat1_desc,
    cat1_img,
    cat2,
    cat2_desc,
    cat2_img,
    cat3,
    cat3_desc,
    cat3_img,
    more_sandy,
    company,
    featured_img_1,
    featured_img_2,
    featured_img_3,
  } = page

  const categories = useMemo(
    () => [
      {
        name: cat1,
        description: cat1_desc,
        imageSrc: pickSrc(cat1_img, HARDCODED_CATEGORY_IMAGES[0]),
        localFallback: LOCAL_CATEGORY_FALLBACKS[0],
        href: '/gallery',
      },
      {
        name: cat2,
        description: cat2_desc,
        imageSrc: pickSrc(cat2_img, HARDCODED_CATEGORY_IMAGES[1]),
        localFallback: LOCAL_CATEGORY_FALLBACKS[1],
        href: '/gallery',
      },
      {
        name: cat3,
        description: cat3_desc,
        imageSrc: pickSrc(cat3_img, HARDCODED_CATEGORY_IMAGES[2]),
        localFallback: LOCAL_CATEGORY_FALLBACKS[2],
        href: '/book',
      },
    ],
    [cat1, cat1_desc, cat1_img, cat2, cat2_desc, cat2_img, cat3, cat3_desc, cat3_img]
  )

  // Featured strip always uses the classic hardcoded set (gallery lives on /gallery).
  // Optional CMS overrides: featured_img_1 / featured_img_2 / featured_img_3
  const featuredImages = useMemo(() => {
    return HARDCODED_FEATURED_IMAGES.map((hardcoded, index) => ({
      src: pickSrc(
        [featured_img_1, featured_img_2, featured_img_3][index],
        hardcoded
      ),
      localFallback: LOCAL_FEATURED_FALLBACKS[index],
    }))
  }, [featured_img_1, featured_img_2, featured_img_3])

  return (
    <>
      <div className="header">
        <HomeHeader title={title} subtitle1={subtitle1} subtitle2={subtitle2} />
      </div>

      <PreorderForm />

      <section className="cats-v2">
        <div className="cats-v2__glow cats-v2__glow--a" aria-hidden="true" />
        <div className="cats-v2__glow cats-v2__glow--b" aria-hidden="true" />
        <div className="cats-v2__glow cats-v2__glow--c" aria-hidden="true" />

        <div className="cats-v2__inner">
          <div className="cats-v2__intro">
            <p className="cats-v2__eyebrow">Find out</p>
            <h2>
              <span className="cats-v2__script">What</span> we do
            </h2>
            <p>Three paths to something sweet. Pick a direction and explore.</p>
          </div>

          <div className="cats-v2__grid">
            {categories.map((category, index) => (
              <Link
                key={category.name}
                to={category.href}
                className={`cats-v2__card cats-v2__card--${index + 1}`}
              >
                <div className="cats-v2__media">
                  <SafeImage
                    src={category.imageSrc}
                    fallbackSrc={category.localFallback}
                    alt={category.name}
                  />
                </div>
                <div className="cats-v2__body">
                  <span className="cats-v2__num">0{index + 1}</span>
                  <h3>{category.name}</h3>
                  <p>{category.description}</p>
                  <em className="cats-v2__link">Explore</em>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="cake-featured home-section">
        <div className="cake-featured-inner">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
            <div className="mx-auto max-w-2xl py-16 sm:py-20 lg:max-w-none lg:py-24">
              <div className="home-section-heading featured-heading">
                <span className="featured-ornament" aria-hidden="true" />
                <h2 className="pannel-header">Featured</h2>
                <h4 className="home-section-kicker">Check out our specials</h4>
                <span className="featured-ornament featured-ornament--flip" aria-hidden="true" />
              </div>

              <div className="featured-stage mt-12">
                <div className="featured-showcase">
                  {featuredImages.map((item, index) => (
                    <figure
                      key={`${item.src}-${index}`}
                      className={`featured-shot featured-shot--${index + 1}`}
                    >
                      <div className="featured-shot__frame">
                        <SafeImage
                          src={item.src}
                          fallbackSrc={item.localFallback}
                          alt={`Featured bake ${index + 1}`}
                          className="featured-shot__img"
                        />
                      </div>
                      {index === 1 ? (
                        <span className="featured-shot__badge">Signature</span>
                      ) : null}
                    </figure>
                  ))}

                  <aside className="featured-copy">
                    <p className="featured-copy__eyebrow">From the kitchen</p>
                    <h3>More from Sandy&apos;s</h3>
                    <div className="featured-copy__rule" aria-hidden="true" />
                    <p>{more_sandy}</p>
                    <div className="featured-copy__actions">
                      <Link to="/gallery" className="featured-copy__link">
                        View gallery
                      </Link>
                      <Link to="/book" className="featured-copy__cta">
                        Order now
                      </Link>
                    </div>
                  </aside>
                </div>
              </div>

              <div className="featured-bottom">
                <div className="feat-b-item">
                  <SafeImage src={cupcake} fallbackSrc={HARDCODED_CATEGORY_IMAGES[1]} alt="Cupcakes" />
                  <h4>Cupcakes</h4>
                  <p>S / M / L</p>
                </div>
                <div className="feat-b-item">
                  <SafeImage src={wedcake} fallbackSrc={HARDCODED_CATEGORY_IMAGES[0]} alt="Wedding cakes" />
                  <h4>Wedding</h4>
                  <p>Any size</p>
                </div>
                <div className="feat-b-item">
                  <SafeImage src={HARDCODED_BDAY} fallbackSrc={cakeBg} alt="Birthday cakes" />
                  <h4>Birthday</h4>
                  <p>Your size</p>
                </div>
                <div className="feat-b-item">
                  <SafeImage src={wedcake} fallbackSrc={HARDCODED_CATEGORY_IMAGES[2]} alt="Custom cakes" />
                  <h4>Custom</h4>
                  <p>Your vision</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactForm company={company || defaultCompanyValues} />
    </>
  )
}

export default HomePage
